/**
 * Rana hero banner.
 *
 * Reveals its content once the section is in view, and drives an optional
 * pointer-follow parallax on the media. Both are skipped under
 * prefers-reduced-motion.
 */
class RanaHeroBanner extends HTMLElement {
  connectedCallback() {
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.media = this.querySelector('[data-hero-media]');

    // See rana-reveal.js: the CSS only hides content once this is present.
    this.classList.add('is-ready');

    if (this.reduced) {
      this.classList.add('is-visible');
      return;
    }

    // Stagger each piece of content by its position in the stack.
    this.querySelectorAll('[data-hero-reveal]').forEach((el, i) => {
      el.style.setProperty('--rana-hero-reveal-index', String(i));
    });

    this.#observe();

    if (this.hasAttribute('data-parallax') && this.media) {
      this.#bindParallax();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.onPointer) window.removeEventListener('pointermove', this.onPointer);
  }

  #observe() {
    // Fire once, slightly before the section is fully on screen.
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.classList.add('is-visible');
          this.observer?.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.01 }
    );
    this.observer.observe(this);
  }

  /**
   * Nudges the media a few pixels against the pointer. Movement is written to a
   * custom property and applied with a transition, so it stays smooth without
   * running its own animation frame loop.
   */
  #bindParallax() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.onPointer = (event) => {
      const rect = this.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;

      const x = (event.clientX / window.innerWidth - 0.5) * 2;
      const y = (event.clientY / window.innerHeight - 0.5) * 2;
      this.media.style.setProperty('--rana-hero-shift-x', `${(x * -12).toFixed(2)}px`);
      this.media.style.setProperty('--rana-hero-shift-y', `${(y * -12).toFixed(2)}px`);
    };

    window.addEventListener('pointermove', this.onPointer, { passive: true });
  }
}

if (!customElements.get('rana-hero-banner')) {
  customElements.define('rana-hero-banner', RanaHeroBanner);
}
